import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Baby, Phone, Plus, RefreshCw, UserRound } from 'lucide-react';
import { api } from '@/lib/api';
import { VENUE_EVENTS } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useAppSelector } from '@/Redux/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/states';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import {
  HOST_QUEUE_ROUTES,
  HOST_QUEUE_PERMISSIONS,
  ReservationStatus,
  WaitlistStatus,
  apiMessage,
  isDoubleSeatConflict,
  newOperationId,
  type HostQueueResponse,
  type HostQueueRow,
  type TableOption,
} from '../hostQueueApi';

/** Minutes early (negative) or late (positive) a due time is, right now. */
function minutesFromNow(iso: string): number {
  return Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
}

function dueLabel(row: HostQueueRow, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const delta = minutesFromNow(row.dueAt);
  if (delta > 0) return t('operations.reservations.hostQueue.lateBy', { count: delta });
  return t('operations.reservations.hostQueue.inMinutes', { count: -delta });
}

/**
 * §13 / ADR-0018 — the host desk: reservations due soon and everybody
 * standing at the door, in one ordered list. That merge is the whole point of
 * the epic (`HostQueueService.queue`'s own docstring calls it "the definition
 * of done, as a read") — before this a host held two screens in their head,
 * one of which (the general waitlist) did not exist at all.
 *
 * `GET /waitlist/queue` needs BOTH `waitlist.view` AND `reservations.view` —
 * `RequirePermissions` on the backend is AND, not the dashboard's usual
 * `canAny` — so this panel checks both explicitly rather than reusing the
 * tab's own (necessarily looser) `canAny` gate, and says which is missing
 * rather than attempting a call that is certain to 403.
 */
export default function HostQueuePanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);

  const [joinOpen, setJoinOpen] = useState(false);
  const [joinPartySize, setJoinPartySize] = useState('2');
  const [joinName, setJoinName] = useState('');
  const [joinPhone, setJoinPhone] = useState('');
  const [joinNotes, setJoinNotes] = useState('');
  const [seatTarget, setSeatTarget] = useState<HostQueueRow | null>(null);
  const [seatTableId, setSeatTableId] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const mayReadQueue = can(HOST_QUEUE_PERMISSIONS.waitlistView, HOST_QUEUE_PERMISSIONS.reservationsView);

  const queue = useApi<HostQueueResponse>(
    mayReadQueue && activeBranchId ? HOST_QUEUE_ROUTES.queue : null,
    activeBranchId ? { branchId: activeBranchId, horizonMinutes: 180 } : undefined,
  );

  const tables = useApi<unknown>(
    seatTarget && can(HOST_QUEUE_PERMISSIONS.seat) ? HOST_QUEUE_ROUTES.tables : null,
    activeBranchId ? { branchId: activeBranchId } : undefined,
  );
  const { rows: tableRows } = rowsOf<TableOption>(tables.data);

  useRealtime(VENUE_EVENTS.WAITLIST_JOINED, () => queue.reload());
  useRealtime(VENUE_EVENTS.WAITLIST_CALLED, () => queue.reload());
  useRealtime(VENUE_EVENTS.WAITLIST_SEATED, () => queue.reload());
  useRealtime(VENUE_EVENTS.WAITLIST_CLOSED, () => queue.reload());
  useRealtime(VENUE_EVENTS.RESERVATION_BOOKED, () => queue.reload());
  useRealtime(VENUE_EVENTS.RESERVATION_SEATED, () => queue.reload());
  useRealtimeResync(() => queue.reload());

  if (!activeBranchId) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <p className="text-sm font-medium">{t('operations.reservations.hostQueue.noBranchTitle')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('operations.reservations.hostQueue.noBranchHint')}</p>
      </div>
    );
  }

  if (!mayReadQueue) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <p className="text-sm font-medium">{t('operations.reservations.hostQueue.needsBothTitle')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('operations.reservations.hostQueue.needsBothHint')}</p>
      </div>
    );
  }

  const call = async (row: HostQueueRow) => {
    setBusyId(row.id);
    try {
      await api.post(HOST_QUEUE_ROUTES.call(row.id));
      toast.success(t('operations.reservations.hostQueue.called'));
      queue.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const noShow = async (row: HostQueueRow) => {
    setBusyId(row.id);
    try {
      await api.post(HOST_QUEUE_ROUTES.noShow(row.id));
      toast.success(t('operations.reservations.hostQueue.markedNoShow'));
      queue.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const removeFromQueue = async (row: HostQueueRow) => {
    setBusyId(row.id);
    try {
      await api.post(HOST_QUEUE_ROUTES.cancel(row.id), {});
      toast.success(t('operations.reservations.hostQueue.removed'));
      queue.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const seatReservation = async (row: HostQueueRow) => {
    setBusyId(row.id);
    try {
      await api.post(HOST_QUEUE_ROUTES.reservationSeat(row.id), {});
      toast.success(t('operations.reservations.seated'));
      queue.reload();
    } catch (e) {
      if (isDoubleSeatConflict(e)) {
        toast.error(t('operations.reservations.hostQueue.someoneJustSeated'));
        queue.reload();
      } else {
        toast.error(apiMessage(e, t('operations.errors.actionFailed')));
      }
    } finally {
      setBusyId(null);
    }
  };

  const submitJoin = async () => {
    const partySize = Number(joinPartySize);
    if (!Number.isInteger(partySize) || partySize < 1) return;
    setBusyId('join');
    try {
      await api.post(HOST_QUEUE_ROUTES.waitlist, {
        branchId: activeBranchId,
        partySize,
        guestName: joinName.trim() || undefined,
        guestPhone: joinPhone.trim() || undefined,
        notes: joinNotes.trim() || undefined,
        operationId: newOperationId('waitlist-join'),
      });
      toast.success(t('operations.reservations.hostQueue.joined'));
      setJoinOpen(false);
      setJoinPartySize('2');
      setJoinName('');
      setJoinPhone('');
      setJoinNotes('');
      queue.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const submitSeat = async () => {
    if (!seatTarget || !seatTableId) return;
    setBusyId(seatTarget.id);
    try {
      await api.post(HOST_QUEUE_ROUTES.seat(seatTarget.id), {
        tableId: seatTableId,
        operationId: newOperationId('waitlist-seat'),
      });
      toast.success(t('operations.reservations.seated'));
      setSeatTarget(null);
      setSeatTableId('');
      queue.reload();
    } catch (e) {
      if (isDoubleSeatConflict(e)) {
        // The database, not a validation message: this table's UNIQUE open
        // session was already taken by the time this request landed.
        toast.error(t('operations.reservations.hostQueue.someoneJustSeated'));
        setSeatTarget(null);
        setSeatTableId('');
        queue.reload();
        tables.reload();
      } else {
        toast.error(apiMessage(e, t('operations.errors.actionFailed')));
      }
    } finally {
      setBusyId(null);
    }
  };

  const rows = queue.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4 text-sm text-ink-muted">
          <span>{t('operations.reservations.hostQueue.reservationsDue', { count: queue.data?.counts.reservations ?? 0 })}</span>
          <span>{t('operations.reservations.hostQueue.walkInsWaiting', { count: queue.data?.counts.walkIns ?? 0 })}</span>
          <span className="tabular-nums">{t('operations.reservations.hostQueue.covers', { count: queue.data?.counts.covers ?? 0 })}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={queue.reload}>
            <Icon as={RefreshCw} />
            {t('common.refresh')}
          </Button>
          {can(HOST_QUEUE_PERMISSIONS.join) && (
            <Button size="sm" onClick={() => setJoinOpen(true)}>
              <Icon as={Plus} />
              {t('operations.reservations.hostQueue.addWalkIn')}
            </Button>
          )}
        </div>
      </div>

      <TableWrap>
        {queue.loading ? (
          <LoadingState />
        ) : queue.error ? (
          <div role="alert" className="p-6 text-center text-sm">
            <p className="font-medium text-red-700 dark:text-red-300">
              {t('operations.reservations.hostQueue.loadFailed')}
            </p>
            <p className="mt-1 text-ink-muted">{queue.error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="grid place-items-center gap-1 py-16 text-center">
            <p className="text-sm font-medium">{t('operations.reservations.hostQueue.emptyTitle')}</p>
            <p className="text-sm text-ink-muted">{t('operations.reservations.hostQueue.emptyHint')}</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('operations.reservations.hostQueue.columns.kind')}</TH>
                <TH>{t('operations.reservations.hostQueue.columns.guest')}</TH>
                <TH className="text-end">{t('operations.reservations.hostQueue.columns.party')}</TH>
                <TH>{t('operations.reservations.hostQueue.columns.due')}</TH>
                <TH>{t('operations.reservations.hostQueue.columns.status')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <TR key={`${row.kind}-${row.id}`}>
                  <TD>
                    <Badge tone={row.kind === 'RESERVATION' ? 'brand' : 'info'} className="gap-1">
                      <Icon as={row.kind === 'RESERVATION' ? UserRound : Baby} size="xs" />
                      {t(`operations.reservations.hostQueue.kind.${row.kind}`)}
                    </Badge>
                    {row.position != null && (
                      <span className="ms-1 text-xs text-ink-muted">#{row.position}</span>
                    )}
                  </TD>
                  <TD>
                    <p className="font-medium">{row.guestName ?? t('operations.reservations.hostQueue.noName')}</p>
                    {row.guestPhone && (
                      <p className="flex items-center gap-1 text-xs text-ink-muted">
                        <Icon as={Phone} size="xs" />
                        {row.guestPhone}
                      </p>
                    )}
                  </TD>
                  <TD className="text-end tabular-nums">{row.partySize}</TD>
                  <TD className="text-xs">{dueLabel(row, t)}</TD>
                  <TD>
                    <Badge tone={row.status === WaitlistStatus.CALLED ? 'warning' : 'neutral'}>
                      {t(`operations.reservations.hostQueue.status.${row.status}`)}
                    </Badge>
                  </TD>
                  <TD>
                    <div className="flex flex-wrap justify-end gap-1">
                      {row.kind === 'WALK_IN' && row.status === WaitlistStatus.WAITING && can(HOST_QUEUE_PERMISSIONS.call) && (
                        <Button size="sm" variant="outline" onClick={() => call(row)} disabled={busyId === row.id}>
                          {t('operations.reservations.hostQueue.call')}
                        </Button>
                      )}
                      {row.kind === 'WALK_IN' &&
                        (row.status === WaitlistStatus.WAITING || row.status === WaitlistStatus.CALLED) &&
                        can(HOST_QUEUE_PERMISSIONS.seat) && (
                          <Button size="sm" onClick={() => setSeatTarget(row)} disabled={busyId === row.id}>
                            {t('operations.reservations.seat')}
                          </Button>
                        )}
                      {row.kind === 'WALK_IN' && row.status === WaitlistStatus.CALLED && can(HOST_QUEUE_PERMISSIONS.cancel) && (
                        <Button size="sm" variant="ghost" onClick={() => noShow(row)} disabled={busyId === row.id}>
                          {t('operations.reservations.hostQueue.noShow')}
                        </Button>
                      )}
                      {row.kind === 'WALK_IN' && can(HOST_QUEUE_PERMISSIONS.cancel) && (
                        <Button size="sm" variant="ghost" onClick={() => removeFromQueue(row)} disabled={busyId === row.id}>
                          {t('common.remove')}
                        </Button>
                      )}
                      {row.kind === 'RESERVATION' &&
                        (row.status === ReservationStatus.PENDING || row.status === ReservationStatus.CONFIRMED) &&
                        can(HOST_QUEUE_PERMISSIONS.reservationsUpdate) && (
                          row.tableId ? (
                            <Button size="sm" onClick={() => seatReservation(row)} disabled={busyId === row.id}>
                              {t('operations.reservations.seat')}
                            </Button>
                          ) : (
                            <span className="text-xs text-ink-muted">{t('operations.reservations.hostQueue.needsTable')}</span>
                          )
                        )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </TableWrap>

      <Dialog
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        title={t('operations.reservations.hostQueue.addWalkIn')}
        className="max-w-sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setJoinOpen(false)} disabled={busyId === 'join'}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitJoin} disabled={busyId === 'join' || !joinPartySize.trim()}>
              {t('operations.reservations.hostQueue.addToQueue')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label={t('operations.reservations.guestCount')} required controlId="waitlist-party-size">
            <Input inputMode="numeric" value={joinPartySize} onChange={(e) => setJoinPartySize(e.target.value)} />
          </Field>
          <Field label={t('operations.reservations.hostQueue.guestName')} controlId="waitlist-name">
            <Input value={joinName} onChange={(e) => setJoinName(e.target.value)} />
          </Field>
          <Field label={t('operations.reservations.hostQueue.guestPhone')} controlId="waitlist-phone">
            <Input value={joinPhone} onChange={(e) => setJoinPhone(e.target.value)} />
          </Field>
          <Field label={t('operations.common.notes')} controlId="waitlist-notes">
            <Textarea rows={2} value={joinNotes} onChange={(e) => setJoinNotes(e.target.value)} />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={Boolean(seatTarget)}
        onClose={() => {
          setSeatTarget(null);
          setSeatTableId('');
        }}
        title={t('operations.reservations.hostQueue.seatTitle', { name: seatTarget?.guestName ?? '' })}
        className="max-w-sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setSeatTarget(null)} disabled={busyId === seatTarget?.id}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitSeat} disabled={!seatTableId || busyId === seatTarget?.id}>
              {t('operations.reservations.seat')}
            </Button>
          </>
        }
      >
        <Field label={t('operations.reservations.hostQueue.chooseTable')} required controlId="waitlist-seat-table">
          <Select value={seatTableId} onChange={(e) => setSeatTableId(e.target.value)}>
            <option value="">{t('operations.reservations.hostQueue.chooseTablePlaceholder')}</option>
            {tableRows.map((table) => (
              <option key={table.id} value={table.id}>
                {table.number} · {table.seats} {table.area ? `(${table.area.name})` : ''}
              </option>
            ))}
          </Select>
        </Field>
      </Dialog>
    </div>
  );
}
